import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchDisruptionRosterPage } from '@/lib/disruptions/api';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { buildDesk, type DeskState } from './desk-model';

// Data hook for the Hotels desk: walks the disruption roster ONCE (cursor pages,
// capped) and folds it into the desk model. Lives under the roster's own query
// prefix (`['disruptions', urn, 'roster', …]`) so every existing invalidation —
// the WS CaseChanged/progress pushes, allocate/voucher onSuccess — refreshes the
// desk for free; no new liveness wiring.

const DESK_MAX_PAGES = 20;

export interface HotelDeskQuery {
  desk: DeskState | null;
  isLoading: boolean;
  isError: boolean;
  /** True when the roster had more pages than the walk cap — counts are a floor. */
  truncated: boolean;
  refetch: () => void;
}

export function useHotelDesk(disruptionUrn: string | undefined): HotelDeskQuery {
  const qc = useQueryClient();
  const query = useQuery<{ rows: DisruptionPnrGroupRow[]; truncated: boolean }>({
    queryKey: ['disruptions', disruptionUrn, 'roster', 'desk-walk'],
    enabled: !!disruptionUrn,
    staleTime: 15_000,
    queryFn: async () => {
      const rows: DisruptionPnrGroupRow[] = [];
      let cursor: string | undefined;
      let truncated = false;
      for (let page = 0; page < DESK_MAX_PAGES; page += 1) {
        const res = await fetchDisruptionRosterPage(disruptionUrn!, {
          ...(cursor ? { cursor } : {}),
        });
        rows.push(...(res.items ?? []));
        cursor = res.nextCursor ?? undefined;
        if (!cursor) break;
        if (page === DESK_MAX_PAGES - 1) truncated = true;
      }
      return { rows, truncated };
    },
  });

  const desk = useMemo(
    () => (query.data ? buildDesk(query.data.rows) : null),
    [query.data],
  );

  return {
    desk,
    isLoading: query.isLoading,
    isError: query.isError,
    truncated: query.data?.truncated ?? false,
    refetch: () => {
      void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'roster'] });
    },
  };
}
